/**
 * Notification API client — domain-specific functions for the notification feed.
 *
 * Requirements: 3.1, 3.5, 3.6, 3.8, 4.7
 */

import * as api from '../services/api-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface NotificationItem {
  readonly notificationId: string;
  readonly type: string;
  readonly message: string;
  readonly boardId: string;
  readonly cardId: string | null;
  readonly pageId?: string | null;
  readonly libraryId?: string | null;
  readonly actorName: string;
  readonly actorUserId: string;
  readonly read: boolean;
  readonly createdAt: string;
}

export interface PaginatedNotifications {
  readonly data: NotificationItem[];
  readonly pagination: {
    readonly nextCursor: string | null;
    readonly hasMore: boolean;
  };
}

export interface UnreadCountResponse {
  readonly count: number;
}

export interface MarkReadResponse {
  readonly success: true;
}

export interface MarkAllReadResponse {
  readonly success: true;
  readonly count: number;
}

// ---------------------------------------------------------------------------
// API Functions
// ---------------------------------------------------------------------------

/**
 * Fetches the authenticated user's notification feed with cursor-based pagination.
 *
 * @param cursor - Opaque pagination cursor from a previous response's nextCursor
 * @param limit - Number of items per page (1–100, default 25)
 */
export async function getNotifications(
  cursor?: string,
  limit?: number
): Promise<PaginatedNotifications> {
  const query: Record<string, string> = {};
  if (cursor) {
    query.cursor = cursor;
  }
  if (limit !== undefined) {
    query.limit = String(limit);
  }
  return api.get<PaginatedNotifications>('/v1/notifications', query);
}

/**
 * Fetches the authenticated user's unread notification count.
 */
export async function getUnreadCount(): Promise<number> {
  const response = await api.get<UnreadCountResponse>('/v1/notifications/unread-count');
  return response.count;
}

/**
 * Marks a single notification as read.
 *
 * @param notificationId - The ID of the notification to mark as read
 */
export async function markRead(notificationId: string): Promise<MarkReadResponse> {
  return api.put<MarkReadResponse>(`/v1/notifications/${encodeURIComponent(notificationId)}/read`);
}

/**
 * Marks all unread notifications for the authenticated user as read.
 * Returns the count of notifications that were marked.
 */
export async function markAllRead(): Promise<MarkAllReadResponse> {
  return api.put<MarkAllReadResponse>('/v1/notifications/read-all');
}
